import type { StudioTool } from "../comic/comicTypes";
import type { ChildControls, ParentCapability, StudioToolLevel } from "./parentTypes";

export function hasParentCapability(
  controls: Readonly<ChildControls>,
  parentUnlocked: boolean,
  capability: ParentCapability,
): boolean {
  if (parentUnlocked) return true;
  switch (capability) {
    case "createDrawing":
      return controls.allowNewDrawings;
    case "createComic":
      return controls.allowNewComics;
    case "useActivities":
      return controls.allowActivities;
    case "deleteProject":
      return controls.allowProjectDeletion;
    case "importBackup":
      return controls.allowBackupImport;
    case "exportBackup":
      return controls.allowBackupExport;
    case "useCreativeTools":
      return controls.studioToolLevel === "creative" || controls.studioToolLevel === "full";
    case "useFullTools":
      return controls.studioToolLevel === "full";
    case "destructiveEdit":
      return controls.allowDestructiveEditing;
  }
}

export function effectiveStudioToolLevel(
  controls: Readonly<ChildControls>,
  parentUnlocked: boolean,
): StudioToolLevel {
  return parentUnlocked ? "full" : controls.studioToolLevel;
}

export function isStudioToolAllowed(tool: StudioTool, level: StudioToolLevel): boolean {
  if (tool === "pencil" || tool === "marker" || tool === "crayon" || tool === "eraser") {
    return true;
  }
  if (
    tool === "fill" ||
    tool === "pickColour" ||
    tool === "line" ||
    tool === "rectangle" ||
    tool === "ellipse"
  ) {
    return level === "creative" || level === "full";
  }
  return level === "full";
}

export interface ToolSettlement {
  shouldCancel: boolean;
  nextTool: StudioTool;
}

export interface StudioPermissionSnapshot {
  toolLevel: StudioToolLevel;
  destructive: boolean;
  parentUnlocked: boolean;
}

export function settleToolForPermission(
  activeTool: StudioTool,
  level: StudioToolLevel,
): ToolSettlement {
  return isStudioToolAllowed(activeTool, level)
    ? { shouldCancel: false, nextTool: activeTool }
    : { shouldCancel: true, nextTool: "pencil" };
}

export function settleStudioPolicyChange(
  previous: Readonly<StudioPermissionSnapshot>,
  next: Readonly<StudioPermissionSnapshot>,
  activeTool: StudioTool,
): ToolSettlement {
  const ranks: Record<StudioToolLevel, number> = { basic: 0, creative: 1, full: 2 };
  const toolSettlement = settleToolForPermission(activeTool, next.toolLevel);
  return {
    shouldCancel:
      (previous.parentUnlocked && !next.parentUnlocked) ||
      ranks[next.toolLevel] < ranks[previous.toolLevel] ||
      (previous.destructive && !next.destructive) ||
      toolSettlement.shouldCancel,
    nextTool: toolSettlement.nextTool,
  };
}

export function shouldRevokeFullToolAccess(
  previous: Readonly<StudioPermissionSnapshot>,
  next: Readonly<StudioPermissionSnapshot>,
): boolean {
  return (
    (previous.parentUnlocked && !next.parentUnlocked) ||
    (previous.toolLevel === "full" && next.toolLevel !== "full")
  );
}
